import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";
import CheckIcon from "app/components/Icons/CheckIcon";
import { CloseIcon } from "app/components/Icons/CloseIcon";
import DragIcon from "app/components/Icons/DragIcon";
import EditIcon from "app/components/Icons/EditIcon";
import TrashIcon from "app/components/Icons/TrashIcon";
import "app/components/TextEditor/components/tiptap-node/image-resize-node/image-resize-node.scss";
import React, { useCallback, useEffect, useRef, useState } from "react";

const splitFileName = (name: string) => {
  const lastDot = name.lastIndexOf(".");
  if (lastDot <= 0) {
    return { baseName: name, extension: "" };
  }
  return {
    baseName: name.slice(0, lastDot),
    extension: name.slice(lastDot),
  };
};

type ResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

interface ResizeState {
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  direction: ResizeDirection;
  originalAspectRatio: number;
}

/**
 * React component that renders an image with resize handles.
 * Users can drag the handles to resize the image while maintaining aspect ratio.
 */
export const ImageResizeNode: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  editor,
  extension,
  deleteNode,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [resizing, setResizing] = useState(false);
  const [dimensions, setDimensions] = useState<{
    width: number | null;
    height: number | null;
  }>({
    width: node.attrs.width ? parseInt(node.attrs.width) : null,
    height: node.attrs.height ? parseInt(node.attrs.height) : null,
  });
  const resizeStateRef = useRef<ResizeState | null>(null);

  const allowResize = node.attrs.allowResize ?? true;
  const maintainAspectRatio = node.attrs.maintainAspectRatio ?? true;
  const minWidth = node.attrs.minWidth ?? 50;
  const minHeight = node.attrs.minHeight ?? 50;
  // Node attrs can become stale across mode toggles; use editor runtime state.
  const isEditable = editor.isEditable && (node.attrs.isEditable ?? true);
  const imageId = node.attrs.imageId as string | null;
  const fileName = (node.attrs.title || node.attrs.alt || "Attachment") as string;
  const { baseName, extension: fileExtension } = splitFileName(fileName);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(baseName);
  const [showImagePreview, setShowImagePreview] = useState(true);
  const renameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setRenameValue(splitFileName(fileName).baseName);
  }, [fileName]);

  useEffect(() => {
    setShowImagePreview(true);
  }, [node.attrs.src]);

  useEffect(() => {
    if (isRenaming) {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    }
  }, [isRenaming]);

  const handleRenameStart = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setRenameValue(baseName);
    setIsRenaming(true);
  };

  const handleRenameCancel = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    setRenameValue(baseName);
    setIsRenaming(false);
  };

  const handleRenameSubmit = (event?: React.MouseEvent) => {
    event?.preventDefault();
    event?.stopPropagation();

    const trimmed = renameValue.trim();
    const nextFileName = trimmed
      ? `${trimmed}${fileExtension}`
      : fileName;

    if (!trimmed || nextFileName === fileName) {
      handleRenameCancel();
      return;
    }

    if (!imageId) {
      updateAttributes({ title: nextFileName, alt: nextFileName });
      setIsRenaming(false);
      return;
    }

    extension.options.onAttachmentRename?.({
      imageId,
      fileName: nextFileName,
      onRenamed: () => {
        updateAttributes({ title: nextFileName, alt: nextFileName });
        setIsRenaming(false);
      },
    });
  };

  const handlePreview = (event: React.MouseEvent) => {
    if (isRenaming) return;

    event.preventDefault();
    event.stopPropagation();

    extension.options.onAttachmentClick?.({
      url: node.attrs.src,
      imageId: imageId || undefined,
    });
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    deleteNode();

    if (imageId) {
      extension.options.onAttachmentDelete?.({ imageId });
    }
  };

  // Handle image load to get natural dimensions
  const handleImageLoad = useCallback(() => {
    if (imageRef.current && !dimensions.width && !dimensions.height) {
      // const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;
      const initialDimensions = {
        width: 450,
        height: naturalHeight,
      };
      setDimensions(initialDimensions);
      updateAttributes(initialDimensions);
    }
  }, [dimensions, updateAttributes]);

  // Calculate new dimensions based on mouse movement
  const calculateNewDimensions = (
    clientX: number,
    clientY: number,
    state: ResizeState,
  ): { width: number; height: number } => {
    const deltaX = clientX - state.startX;
    const deltaY = clientY - state.startY;

    let newWidth = state.startWidth;
    let newHeight = state.startHeight;

    const { direction, originalAspectRatio } = state;

    // Handle horizontal resizing
    if (direction.includes("e")) {
      newWidth = Math.max(minWidth, state.startWidth + deltaX);
    }
    if (direction.includes("w")) {
      newWidth = Math.max(minWidth, state.startWidth - deltaX);
    }

    // Handle vertical resizing
    if (direction.includes("s")) {
      newHeight = Math.max(minHeight, state.startHeight + deltaY);
    }
    if (direction.includes("n")) {
      newHeight = Math.max(minHeight, state.startHeight - deltaY);
    }

    // Maintain aspect ratio if enabled
    if (maintainAspectRatio) {
      if (
        direction.includes("e") ||
        direction.includes("w") ||
        (direction !== "n" && direction !== "s")
      ) {
        newHeight = Math.round(newWidth / originalAspectRatio);
      } else if (direction.includes("n") || direction.includes("s")) {
        newWidth = Math.round(newHeight * originalAspectRatio);
      }
    }

    return { width: newWidth, height: newHeight };
  };

  // Start resizing
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, direction: ResizeDirection) => {
      if (!allowResize || !isEditable) return;

      e.preventDefault();
      e.stopPropagation();

      const currentWidth = dimensions.width || 300;
      const currentHeight = dimensions.height || 300;
      const aspectRatio = currentWidth / currentHeight;

      resizeStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startWidth: currentWidth,
        startHeight: currentHeight,
        direction,
        originalAspectRatio: aspectRatio,
      };

      setResizing(true);
    },
    [allowResize, dimensions, isEditable],
  );

  // Handle mouse move during resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizing || !resizeStateRef.current) return;

      const newDimensions = calculateNewDimensions(
        e.clientX,
        e.clientY,
        resizeStateRef.current,
      );

      setDimensions({
        width: newDimensions.width,
        height: newDimensions.height,
      });
    };

    const handleMouseUp = () => {
      if (!resizing || !resizeStateRef.current) return;

      // Update attributes with final dimensions
      updateAttributes({
        width: dimensions.width,
        height: dimensions.height,
      });

      setResizing(false);
      resizeStateRef.current = null;
    };

    if (resizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [resizing, dimensions, updateAttributes]);

  return (
    <NodeViewWrapper
      as="div"
      className="image-resize-node group"
      data-selected={isEditable ? selected : false}
      data-image-id={node.attrs.imageId}
    >
      <div className="attachment-header">
        {isRenaming && isEditable ? (
          <div
            className="attachment-header-rename"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="attachment-header-rename-field">
              <input
                ref={renameInputRef}
                value={renameValue}
                onChange={(event) => setRenameValue(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleRenameSubmit();
                  }
                  if (event.key === "Escape") {
                    event.preventDefault();
                    handleRenameCancel();
                  }
                }}
                className="attachment-header-rename-input"
                aria-label="Rename attachment"
              />
              {fileExtension ? (
                <span className="attachment-header-extension">{fileExtension}</span>
              ) : null}
            </div>
            <button
              type="button"
              className="attachment-header-action"
              onClick={handleRenameSubmit}
              aria-label="Confirm rename"
            >
              <CheckIcon size={16} color="#5A8DF1" />
            </button>
            <button
              type="button"
              className="attachment-header-action"
              onClick={handleRenameCancel}
              aria-label="Cancel rename"
            >
              <CloseIcon size="16" color="#79808A" />
            </button>
          </div>
        ) : (
          <div className="attachment-header-display">
            {isEditable ? (
              <span
                className="attachment-drag-handle"
                data-drag-handle
                onPointerDown={(event) => event.stopPropagation()}
              >
                <DragIcon size={16} color="#79808A" />
              </span>
            ) : null}
            <button
              type="button"
              className="attachment-header-label"
              onClick={handlePreview}
              onPointerDown={(event) => event.stopPropagation()}
            >
              {fileName}
            </button>
            {isEditable ? (
              <div
                className="attachment-header-actions"
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="attachment-header-action"
                  onClick={handleRenameStart}
                  aria-label="Rename attachment"
                >
                  <EditIcon size={16} color="#79808A" />
                </button>
                <button
                  type="button"
                  className="attachment-header-action"
                  onClick={handleDelete}
                  aria-label="Delete attachment"
                >
                  <TrashIcon size={16} color="#79808A" />
                </button>
              </div>
            ) : null}
          </div>
        )}
      </div>
      {showImagePreview ? (
        <div
          ref={containerRef}
          className="image-resize-container"
          data-drag-handle
          style={{
            width: dimensions.width ? `${dimensions.width}px` : "100%",
            height: "100%",
          }}
        >
          <img
            ref={imageRef}
            src={node.attrs.src}
            data-image-id={node.attrs.imageId}
            alt=""
            onLoad={handleImageLoad}
            onError={() => setShowImagePreview(false)}
            onClick={handlePreview}
            className="image-resize-image"
            draggable={true}
          />

          {allowResize && selected && isEditable && (
            <>
              {/* Edge handles */}
              <div className="handleContainer">
                <div
                  className="resize-handle-w"
                  onMouseDown={(e) => handleMouseDown(e, "w")}
                  title="Resize (left)"
                />
                <div
                  className="resize-handle-e"
                  onMouseDown={(e) => handleMouseDown(e, "e")}
                  title="Resize (right)"
                />
              </div>
            </>
          )}
        </div>
      ) : null}
    </NodeViewWrapper>
  );
};
